package twist.mena.sports.admin.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.NavState
import twist.mena.sports.admin.state.SHOW_REVAMP
import twist.mena.sports.admin.state.Screen
import twist.mena.sports.admin.theme.*

private val SIDEBAR_WIDTH = 240.px

@Composable
fun AdminLayout(
    isDark: Boolean,
    onToggleTheme: () -> Unit,
    showPreview: Boolean,
    onTogglePreview: () -> Unit,
    content: @Composable () -> Unit
) {
    val colors = AppTheme.colors
    val currentScreen = NavState.currentScreen

    val pageTitle = when (currentScreen) {
        Screen.OVERVIEW -> "Overview"
        Screen.TEAMS -> "Teams"
        Screen.LEAGUES -> "Leagues"
        Screen.FIXTURES -> "Fixtures"
        Screen.PLAYERS -> "Players"
        Screen.SYNC_RUNS -> if (SHOW_REVAMP) "Sync Runs" else "Data Sync"
        Screen.NOTIFICATIONS -> "Notifications"
        Screen.ANALYTICS -> "Analytics"
        Screen.AUTOMATION_TESTS -> "Automation Tests"
        Screen.API_DOCS -> "API Documentation"
        Screen.LEGACY_API_DOCS -> "API Documentation"
        Screen.LEGACY_TEAMS -> "Teams"
        Screen.LEGACY_LEAGUES -> "Leagues"
        Screen.LEGACY_PLAYERS -> "Players"
        Screen.ENTITY_MAPPINGS -> "Entity Mappings"
        Screen.ADS -> "Ads"
        else -> ""
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            minHeight(100.vh)
            backgroundColor(Color(colors.background))
            color(Color(colors.text))
            fontFamily("'Cairo', sans-serif")
            themeTransition()
        }
    }) {
        // Sidebar
        Div({
            style {
                width(SIDEBAR_WIDTH)
                backgroundColor(Color(colors.sidebar))
                padding(Spacing.xl)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("border-right", "1px solid ${colors.border}")
                property("position", "fixed")
                property("top", "0")
                property("bottom", "0")
                property("left", "0")
                property("overflow-y", "auto")
                themeTransition()
            }
        }) {
            // Logo
            Div({
                style {
                    textAlign("center")
                    marginBottom(Spacing.xxl)
                    paddingTop(Spacing.lg)
                }
            }) {
                Img(src = "twist-header-logo.svg", alt = "Twist Sports") {
                    style {
                        width(140.px)
                        property("object-fit", "contain")
                    }
                }
            }

            // Overview
            NavItem("Overview", Screen.OVERVIEW)
            NavItem("Analytics", Screen.ANALYTICS)
            NavItem("Ads", Screen.ADS)

            // Auto-expand the section that contains the active screen
            val isRevampScreen = currentScreen in listOf(
                Screen.TEAMS, Screen.LEAGUES, Screen.FIXTURES, Screen.PLAYERS,
                Screen.SYNC_RUNS, Screen.AUTOMATION_TESTS, Screen.API_DOCS
            )
            val isLegacyScreen = currentScreen in listOf(
                Screen.LEGACY_TEAMS, Screen.LEGACY_LEAGUES, Screen.LEGACY_PLAYERS,
                Screen.NOTIFICATIONS, Screen.LEGACY_API_DOCS, Screen.ENTITY_MAPPINGS
            )

            // Football (legacy data) section
            var legacyExpanded by remember { mutableStateOf(true) }
            val legacyOpen = legacyExpanded || isLegacyScreen
            SectionHeader("Football", legacyOpen) { legacyExpanded = !legacyOpen }
            if (legacyOpen) {
                NavItem("Teams", Screen.LEGACY_TEAMS)
                NavItem("Leagues", Screen.LEGACY_LEAGUES)
                NavItem("Players", Screen.LEGACY_PLAYERS)
                // Links legacy entities to revamp catalog ones, so it is hidden with the revamp
                if (SHOW_REVAMP) NavItem("Entity Mappings", Screen.ENTITY_MAPPINGS)
                NavItem("Notifications", Screen.NOTIFICATIONS)
                // With the revamp hidden, Sync Runs is reduced to the legacy sync and lives here
                if (!SHOW_REVAMP) NavItem("Data Sync", Screen.SYNC_RUNS)
                // The test runner covers auth, favorites and notifications, not just the revamp
                if (!SHOW_REVAMP) NavItem("Automation Tests", Screen.AUTOMATION_TESTS)
                NavItem("API Docs", Screen.LEGACY_API_DOCS)
            }

            // Revamp section
            if (SHOW_REVAMP) {
                var revampExpanded by remember { mutableStateOf(false) }
                val revampOpen = revampExpanded || isRevampScreen
                SectionHeader("Revamp", revampOpen) { revampExpanded = !revampOpen }
                if (revampOpen) {
                    NavItem("Teams", Screen.TEAMS)
                    NavItem("Leagues", Screen.LEAGUES)
                    NavItem("Fixtures", Screen.FIXTURES)
                    NavItem("Players", Screen.PLAYERS)
                    NavItem("Sync Runs", Screen.SYNC_RUNS)
                    NavItem("Automation Tests", Screen.AUTOMATION_TESTS)
                    NavItem("API Docs", Screen.API_DOCS)
                }
            }

            // Spacer
            Div({ style { property("margin-top", "auto") } })

            // Client Preview toggle
            Div({
                onClick { onTogglePreview() }
                style {
                    padding(Spacing.md, Spacing.lg)
                    marginTop(Spacing.lg)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    property("border-top", "1px solid ${colors.border}")
                    paddingTop(Spacing.lg)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.sm)
                    backgroundColor(Color(if (showPreview) colors.primary else "transparent"))
                    color(Color(if (showPreview) "#ffffff" else colors.sidebarText))
                    fontWeight(if (showPreview) "600" else "400")
                    fontSize(Typography.base)
                    property("transition", "background-color 0.2s, color 0.2s")
                }
            }) {
                Span { Text("\uD83D\uDCF1") }
                Text("Client Preview")
            }
        }

        // Main area (offset by sidebar, shrink when preview is shown)
        Div({
            style {
                marginLeft(SIDEBAR_WIDTH)
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                minHeight(100.vh)
                if (showPreview) {
                    marginRight(420.px)
                }
            }
        }) {
            // Topbar
            Topbar(
                pageTitle = pageTitle,
                isDark = isDark,
                onToggleTheme = onToggleTheme,
                userEmail = AuthState.getEmail(),
                onLogout = {
                    AuthState.logout()
                    NavState.navigate(Screen.LOGIN)
                },
                sidebarWidth = SIDEBAR_WIDTH
            )

            // Content area below topbar
            Div({
                style {
                    paddingTop(64.px)
                    padding(88.px, Spacing.xxl, Spacing.xxl, Spacing.xxl)
                    flex(1)
                }
            }) {
                content()
            }
        }

        // Mobile Preview Panel
        if (showPreview) {
            val clientUrl = run {
                val host = kotlinx.browser.window.location.hostname
                when (host) {
                    "scora-stg.twistmena.com" -> "https://sports-stg.twistmena.com"
                    "scora.twistmena.com" -> "https://sports.twistmena.com"
                    else -> "http://localhost:3000"
                }
            }
            MobilePreview(clientUrl = clientUrl)
        }
    }
}

@Composable
private fun SectionHeader(title: String, expanded: Boolean, onToggle: () -> Unit) {
    val colors = AppTheme.colors
    Div({
        onClick { onToggle() }
        style {
            marginTop(Spacing.xl)
            marginBottom(Spacing.sm)
            paddingLeft(Spacing.lg)
            paddingRight(Spacing.lg)
            fontSize(Typography.xs)
            color(Color(colors.textMuted))
            fontWeight("600")
            property("text-transform", "uppercase")
            property("letter-spacing", "0.05em")
            cursor("pointer")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
        }
    }) {
        Text(title)
        Span({
            style {
                fontSize(Typography.xs)
                property("transition", "transform 0.2s")
                property("transform", if (expanded) "rotate(90deg)" else "rotate(0deg)")
            }
        }) { Text("▶") }
    }
}

@Composable
private fun NavItem(label: String, screen: Screen) {
    val colors = AppTheme.colors
    val isActive = NavState.currentScreen == screen

    Div({
        onClick { NavState.navigate(screen) }
        style {
            padding(Spacing.md, Spacing.lg)
            marginBottom(Spacing.xs)
            borderRadius(BorderRadius.md)
            cursor("pointer")
            backgroundColor(Color(if (isActive) colors.sidebarActive else "transparent"))
            color(Color(if (isActive) colors.primary else colors.sidebarText))
            fontWeight(if (isActive) "600" else "400")
            fontSize(Typography.base)
            property("transition", "background-color 0.2s, color 0.2s")
        }
    }) {
        Text(label)
    }
}
